import Toggle from '../Toggle';
import TextInput from '../TextInput';
import Questions, { QuestionType } from '../../lib/Questions';
import EditDisplayTextForm from './edit/EditDisplayTextForm';
import EditVideoEmbedForm from './edit/EditVideoEmbedForm';
import EditThankYouForm from './edit/EditThankYouForm';
import EditSchedulingForm from './edit/EditSchedulingForm';
import EditAddonForm from './edit/EditAddonForm';
import EditAgreeToTermsForm from './edit/EditAgreeToTermsForm';
import EditContactInfoForm from './edit/EditContactInfoForm';
import EditPackagesQuestionForm from './edit/EditPackagesQuestionForm';
import { useMemo } from 'react';
import type { QuestionSettings } from '~/lib/Questions/Questions.types';

import EditPaymentQuestionForm from './edit/EditPaymentQuestionForm';
import EditVariablePaymentQuestionForm from './edit/EditVariablePaymentQuestionForm';
import EditHiddenForm from './edit/EditHiddenForm';
import CollapsiblePanel from '../CollapsiblePanel';
import OptionToggle from '../OptionToggle';
import EditClassSchedulingForm from '~/components/forms/edit/EditClassSchedulingForm';
import EditClientAccountForm from '~/components/forms/edit/EditClientAccountForm';
import EditGiftCertificateForm from '~/components/forms/edit/EditGiftCertificateForm';
import EditTextForm from '~/components/forms/edit/EditTextForm';
import EditInvoiceHeaderForm from '~/components/forms/edit/EditInvoiceHeaderForm';
import EditInvoiceLineItemsForm from '~/components/forms/edit/EditInvoiceLineItemsForm';
import EditInvoiceSummaryForm from '~/components/forms/edit/EditInvoiceSummaryForm';
import EditBillingInfoForm from '~/components/forms/edit/EditBillingInfoForm';
import EditCustomQuestionForm from '~/components/forms/edit/EditCustomQuestionForm';
import { useCustomQuestion } from '~/lib/CustomQuestions/CustomQuestionRegistry';

type EditQuestionFormProps = {
    label: string;
    type?: QuestionType;
    setLabel: (label: string) => void;
    settings: QuestionSettings;
    setSettings: (settings: QuestionSettings) => void;
    formDetails: any;
    question: any;
    url: string;
};

const EditQuestionForm = ({
    label,
    setLabel,
    settings,
    setSettings,
    type,
    formDetails,
    question,
    url,
}: EditQuestionFormProps) => {
    const customQuestion = useCustomQuestion(settings?.customQuestionId);
    const questionSchema = Questions[type as string];

    // Every hook has to run on every render, so nothing may return above this
    //  one. The panel re-renders without a question type as it closes, and
    //  bailing out before a hook is what "rendered fewer hooks than expected"
    //  means
    const extraComponent = useMemo(() => {
        switch (type) {
            case QuestionType.Display:
                return (
                    <EditDisplayTextForm
                        settings={settings}
                        setSettings={setSettings}
                    />
                );
            case QuestionType.VideoEmbed:
                return (
                    <EditVideoEmbedForm
                        settings={settings}
                        setSettings={setSettings}
                    />
                );
            case QuestionType.Text:
                return (
                    <EditTextForm
                        settings={settings}
                        setSettings={setSettings}
                    />
                );
            case QuestionType.Scheduling:
                return (
                    <EditSchedulingForm
                        settings={settings}
                        setSettings={setSettings}
                    />
                );
            case QuestionType.ClassScheduling:
                return (
                    <EditClassSchedulingForm
                        settings={settings}
                        setSettings={setSettings}
                    />
                );
            case QuestionType.Packages:
                return (
                    <EditPackagesQuestionForm
                        settings={settings}
                        setSettings={setSettings}
                    />
                );
            case QuestionType.ThankYou:
                return (
                    <EditThankYouForm
                        settings={settings}
                        setSettings={setSettings}
                        hasScheduling={formDetails?.hasScheduling}
                        formDetails={formDetails}
                    />
                );
            case QuestionType.AddOn:
                return (
                    <EditAddonForm
                        settings={settings}
                        setSettings={setSettings}
                    />
                );
            case QuestionType.ContactInfo:
                return (
                    <EditContactInfoForm
                        settings={settings}
                        setSettings={setSettings}
                    />
                );
            case QuestionType.AgreeToTerms:
                return (
                    <EditAgreeToTermsForm
                        settings={settings}
                        setSettings={setSettings}
                    />
                );
            case QuestionType.Payment:
                return (
                    <EditPaymentQuestionForm
                        settings={settings}
                        setSettings={setSettings}
                    />
                );
            case QuestionType.VariablePayment:
                return (
                    <EditVariablePaymentQuestionForm
                        settings={settings}
                        setSettings={setSettings}
                    />
                );
            case QuestionType.Hidden:
                return (
                    <EditHiddenForm
                        settings={settings}
                        setSettings={setSettings}
                        question={question}
                        label={label}
                        url={url}
                    />
                );
            case QuestionType.ClientAccount:
                return (
                    <EditClientAccountForm
                        settings={settings}
                        setSettings={setSettings}
                    />
                );
            case QuestionType.GiftCertificate:
                return (
                    <EditGiftCertificateForm
                        settings={settings}
                        setSettings={setSettings}
                    />
                );
            case QuestionType.InvoiceHeader:
                return (
                    <EditInvoiceHeaderForm
                        settings={settings}
                        setSettings={setSettings}
                    />
                );
            case QuestionType.InvoiceLineItems:
                return (
                    <EditInvoiceLineItemsForm
                        settings={settings}
                        setSettings={setSettings}
                    />
                );
            case QuestionType.InvoiceSummary:
                return (
                    <EditInvoiceSummaryForm
                        settings={settings}
                        setSettings={setSettings}
                    />
                );
            case QuestionType.BillingInfo:
                return (
                    <EditBillingInfoForm
                        settings={settings}
                        setSettings={setSettings}
                    />
                );
            case QuestionType.Custom:
                return (
                    <EditCustomQuestionForm
                        settings={settings}
                        setSettings={setSettings}
                    />
                );
        }
    }, [settings, setSettings, type, label]);

    if (!questionSchema) {
        return null;
    }

    const showOptions = questionSchema?.hasOptions;

    // A custom question says for itself whether it has a label to set or an
    //  answer to require - the shared type entry can't know
    const hidesLabel =
        questionSchema.noLabel || !!customQuestion?.flags?.noLabel;
    const hidesRequired =
        questionSchema.noRequired ||
        !!customQuestion?.flags?.noRequired ||
        !!customQuestion?.flags?.alwaysRequired;

    return (
        <>
            <div>
                {!hidesLabel && (
                    <TextInput
                        label="Label"
                        inputClassName="w-full"
                        value={label}
                        onChange={setLabel}
                        autoFocus={!label}
                    />
                )}

                {!hidesRequired && (
                    <div className="flex justify-end items-center mt-2">
                        <div className="text-gray-500 text-sm mr-2">
                            This question is required
                        </div>
                        <Toggle
                            enabled={!!settings?.required}
                            setEnabled={enabled =>
                                setSettings({ ...settings, required: enabled })
                            }
                        />
                    </div>
                )}

                {showOptions && (
                    <div>
                        <div className="input-label">Options</div>
                        <textarea
                            value={settings?.options?.join('\n')}
                            onChange={e => {
                                const newOptions = e.target.value.split('\n');
                                setSettings({
                                    ...settings,
                                    options: newOptions,
                                });
                            }}
                            className="w-full min-h-32"
                            required={true}
                        />
                        <div className="text-gray-500 text-sm mt-1 ml-4">
                            A line-separated list of options
                        </div>
                    </div>
                )}

                {extraComponent}
                {!questionSchema.hideExcludePrefill && (
                    <CollapsiblePanel className="mt-6">
                        <div className="pl-6 py-4">
                            {!!formDetails?.hasClientLogin && (
                                <OptionToggle
                                    value={!settings?.excludePrefill}
                                    onChange={enabled =>
                                        setSettings({
                                            ...settings,
                                            excludePrefill: !enabled,
                                        })
                                    }
                                    title="Prefill Past Answer"
                                    description="When a client is logged in, prefill this field with their previous answer if available"
                                    className="pr-6"
                                />
                            )}
                            {!!question?.id && (
                                <div
                                    className={`pr-6 ${!!formDetails?.hasClientLogin ? 'border-t mt-4 pt-4' : ''}`}
                                >
                                    Prefill via URL
                                    <div className=" text-sm text-gray-500">
                                        You can also prefill this question from
                                        the URL by adding{' '}
                                        <strong className="font-mono">
                                            ?answer[{question.id}]=VALUE
                                        </strong>
                                    </div>
                                </div>
                            )}
                        </div>
                    </CollapsiblePanel>
                )}
            </div>
        </>
    );
};

export default EditQuestionForm;
